import React, { useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import {
  downloadTestCaseWorkbook,
  uploadTestCaseWorkbook,
} from "../../../useQuery/useTestCaseExcel";

/**
 * Export test cases to Excel for review, and take the reviewed file back.
 *
 * The upload always previews first. An import can create, update and delete test cases, and a
 * reviewer should never find out which of those happened after the fact.
 */

const Stat = ({ label, value, tone = "default" }) => {
  const tones = {
    default: "text-gray-900",
    good: "text-green-700",
    warn: "text-amber-700",
    bad: "text-red-700",
  };
  return (
    <div className="flex flex-col px-3 py-2 border border-gray-200 bg-white min-w-[92px]">
      <span className="text-[10px] uppercase tracking-wider text-gray-400 font-apple">{label}</span>
      <span className={`text-lg font-semibold tabular-nums ${tones[tone]}`}>{value}</span>
    </div>
  );
};

const DiffRow = ({ change }) => (
  <div className="border-b border-gray-100 last:border-b-0 px-3 py-2">
    <div className="flex items-baseline gap-2 flex-wrap">
      <span className="text-xs font-mono text-gray-500">
        {change.id ? `#${change.id}` : "new"}
      </span>
      <span className="text-[13px] text-gray-800 font-medium break-words">{change.testCase}</span>
      {change.row > 0 && <span className="text-[11px] text-gray-400">row {change.row}</span>}
    </div>
    {Object.entries(change.diffs || {}).map(([field, pair]) => (
      <div key={field} className="mt-1 text-xs font-mono flex flex-wrap items-baseline gap-1.5">
        <span className="text-gray-500">{field}</span>
        <span className="text-red-700 line-through break-all">{String(pair[0] || "(empty)")}</span>
        <span className="text-gray-400">&rarr;</span>
        <span className="text-green-700 break-all">{String(pair[1] || "(empty)")}</span>
      </div>
    ))}
  </div>
);

const Problems = ({ title, items, tone }) => {
  if (!items?.length) return null;
  const Icon = tone === "error" ? XCircleIcon : ExclamationTriangleIcon;
  const colour = tone === "error" ? "text-red-700 bg-red-50 border-red-200"
    : "text-amber-800 bg-amber-50 border-amber-200";
  return (
    <div className={`border ${colour} px-3 py-2`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="h-4 w-4 flex-shrink-0" />
        <span className="text-[13px] font-semibold">{title}</span>
      </div>
      <div className="max-h-44 overflow-y-auto space-y-1">
        {items.map((p, i) => (
          <div key={i} className="text-xs font-apple break-words">
            {p.sheet && <span className="font-mono text-[11px] mr-1">{p.sheet}</span>}
            {p.row > 0 && <span className="font-mono text-[11px] mr-1">row {p.row}:</span>}
            {p.message}
          </div>
        ))}
      </div>
    </div>
  );
};

const TestCaseExcelPanel = ({ collectionId, endpointId, companyId, onImported }) => {
  const fileInput = useRef(null);
  const [busy, setBusy] = useState(null); // "export" | "preview" | "apply"
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [deleteMissing, setDeleteMissing] = useState(false);

  const scopeReady = Boolean(collectionId || endpointId);

  const handleExport = async (scope) => {
    setBusy("export");
    try {
      const name = await downloadTestCaseWorkbook({
        collectionId: scope === "collection" ? collectionId : null,
        endpointId: scope === "endpoint" ? endpointId : null,
        companyId,
      });
      toast.success(`Downloaded ${name}`);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not export the test cases");
    } finally {
      setBusy(null);
    }
  };

  const handleFilePicked = async (event) => {
    const picked = event.target.files?.[0];
    event.target.value = ""; // let the same file be chosen again after a failed attempt
    if (!picked) return;

    setFile(picked);
    setBusy("preview");
    try {
      const result = await uploadTestCaseWorkbook({ file: picked, companyId, dryRun: true });
      setReport(result);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not read that workbook");
      setFile(null);
    } finally {
      setBusy(null);
    }
  };

  const handleApply = async () => {
    setBusy("apply");
    try {
      const result = await uploadTestCaseWorkbook({
        file,
        companyId,
        dryRun: false,
        deleteMissing,
      });
      const parts = [];
      if (result.created) parts.push(`${result.created} created`);
      if (result.updated) parts.push(`${result.updated} updated`);
      if (result.deleted) parts.push(`${result.deleted} deleted`);
      toast.success(parts.length ? parts.join(", ") : "No changes to apply");
      close();
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not apply the workbook");
    } finally {
      setBusy(null);
    }
  };

  const close = () => {
    setFile(null);
    setReport(null);
    setDeleteMissing(false);
  };

  const changeCount = (report?.created || 0) + (report?.updated || 0);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Tooltip title={collectionId ? "" : "Pick a collection first"}>
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled={!collectionId || busy === "export"}
            onClick={() => handleExport("collection")}
            startIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
            sx={{ textTransform: "none", fontSize: 13 }}
          >
            Export collection
          </Button>
        </span>
      </Tooltip>

      <Tooltip title={endpointId ? "" : "Pick an endpoint first"}>
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled={!endpointId || busy === "export"}
            onClick={() => handleExport("endpoint")}
            startIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
            sx={{ textTransform: "none", fontSize: 13 }}
          >
            Export endpoint
          </Button>
        </span>
      </Tooltip>

      <Button
        size="small"
        variant="contained"
        disabled={busy === "preview"}
        onClick={() => fileInput.current?.click()}
        startIcon={
          busy === "preview" ? <CircularProgress size={14} /> : <ArrowUpTrayIcon className="h-4 w-4" />
        }
        sx={{ textTransform: "none", fontSize: 13 }}
      >
        Upload reviewed file
      </Button>

      <input
        ref={fileInput}
        type="file"
        accept=".xlsx,.xlsm"
        onChange={handleFilePicked}
        style={{ display: "none" }}
      />

      {!scopeReady && (
        <span className="text-xs text-gray-500 font-apple">
          Select a collection or endpoint to export.
        </span>
      )}

      <Dialog open={Boolean(report)} onClose={close} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: 17, fontWeight: 600 }}>
          {report?.ok ? "Review these changes" : "This workbook cannot be imported"}
          <div className="text-xs text-gray-500 font-normal mt-0.5">{file?.name}</div>
        </DialogTitle>

        <DialogContent dividers>
          <div className="flex gap-2 flex-wrap mb-3">
            <Stat label="Created" value={report?.created ?? 0} tone={report?.created ? "good" : "default"} />
            <Stat label="Updated" value={report?.updated ?? 0} tone={report?.updated ? "warn" : "default"} />
            <Stat label="Unchanged" value={report?.unchanged ?? 0} />
            <Stat
              label="Missing"
              value={report?.pendingDeletions ?? 0}
              tone={report?.pendingDeletions ? "bad" : "default"}
            />
            <Stat label="Errors" value={report?.errorCount ?? 0} tone={report?.errorCount ? "bad" : "good"} />
          </div>

          <div className="space-y-3">
            <Problems title="Errors - nothing will be saved until these are fixed" items={report?.errors} tone="error" />
            <Problems title="Warnings" items={report?.warnings} tone="warn" />

            {report?.ok && changeCount === 0 && (report?.pendingDeletions ?? 0) === 0 && (
              <div className="flex items-center gap-2 border border-green-200 bg-green-50 px-3 py-2 text-[13px] text-green-800">
                <CheckCircleIcon className="h-4 w-4" />
                This file matches what is already saved. Nothing to apply.
              </div>
            )}

            {report?.updateDetails?.length > 0 && (
              <div className="border border-gray-200">
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-[13px] font-semibold text-gray-800">
                  Updates ({report.updateDetails.length})
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {report.updateDetails.map((c, i) => (
                    <DiffRow key={i} change={c} />
                  ))}
                </div>
              </div>
            )}

            {report?.createDetails?.length > 0 && (
              <div className="border border-gray-200">
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-[13px] font-semibold text-gray-800">
                  New test cases ({report.createDetails.length})
                </div>
                <div className="max-h-40 overflow-y-auto">
                  {report.createDetails.map((c, i) => (
                    <DiffRow key={i} change={c} />
                  ))}
                </div>
              </div>
            )}

            {report?.deleteDetails?.length > 0 && (
              <div className="border border-amber-200">
                <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-[13px] font-semibold text-amber-900">
                  In the app but not in this file ({report.deleteDetails.length})
                </div>
                <div className="max-h-40 overflow-y-auto">
                  {report.deleteDetails.map((c, i) => (
                    <DiffRow key={i} change={c} />
                  ))}
                </div>
                <div className="px-3 py-2 border-t border-amber-200">
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={deleteMissing}
                        onChange={(e) => setDeleteMissing(e.target.checked)}
                      />
                    }
                    label={
                      <span className="text-[13px]">
                        Delete these {report.deleteDetails.length} test cases
                      </span>
                    }
                  />
                  <div className="text-xs text-gray-500 font-apple">
                    Leave unticked if you filtered or trimmed rows before saving.
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={close} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              !report?.ok
              || busy === "apply"
              || (changeCount === 0 && !(deleteMissing && report?.pendingDeletions))
            }
            onClick={handleApply}
            startIcon={busy === "apply" ? <CircularProgress size={14} /> : null}
            sx={{ textTransform: "none" }}
          >
            Apply changes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TestCaseExcelPanel;
